<script setup lang="ts">
import {ElButton, ElDialog, ElDivider, ElMessage, ElPopconfirm, ElSegmented} from 'element-plus';
import {
    backup,
    clearMessages,
    devEraseData,
    exportDocumentaryProofOverview,
    exportDocumentaryProofOverviewZip,
    exportKpiMilestonesForAdmin,
    exportReportItemsForAdmin,
    exportReportItemsForAdminCsv,
    expressionDump,
    getCacheStats,
    getDatabaseVersionFromDb,
    getMessages,
    getStateForReporters,
    setAvailabilityForReporters,
    tnComplianceCsvExport,
    verifyDatabase,
} from '@/base/callBackend';
import {
    AvailabilityState,
    CabPeriod,
    formatIntegerAligned,
    formatMemSizeAligned,
    formatPercentageAligned,
    generateDateId,
    PeriodEnum,
} from 'common';
import {computed, onMounted, Ref, ref, watch} from 'vue';
import UnlockedIcon from '@/views/icons/UnlockedIcon.vue';
import KpiGraphDialog from '@/views/admin/KpiGraphDialog.vue';
import LockedIcon from '@/views/icons/LockedIcon.vue';
import {askForTag, downloadFile} from '@/base/utils.js';
import ReadOnlyIcon from '@/views/icons/ReadOnlyIcon.vue';
import {type UploadAntwoord, uploadMetVoortgang, type Voortgang} from '@/base/uploadMetVoortgang.js';
import VoortgangView from '@/views/general/VoortgangView.vue';

const loading        = ref();
const reportingState = ref<AvailabilityState>(AvailabilityState.closed);

const busyDownloadingReportItemsExcel         = ref<boolean>(false);
const busyDownloadingReportItemsCsv           = ref<boolean>(false);
const busyDownloadingKpiMilestones            = ref<boolean>(false);
const busyDownloadingTreekNormCsv             = ref<boolean>(false);
const busyDownloadingDatabase                 = ref<boolean>(false);
const busyDownloadingDatabase_legacy          = ref<boolean>(false);
const busyVerifyDatabase                      = ref<boolean>(false);
const busyDownloadingExprDump                 = ref<boolean>(false);
const busyErasingDatabase                     = ref<boolean>(false);
const busyUploadingDatabase                   = ref<boolean>(false);
const busyDownloadingBackup                   = ref<boolean>(false);
const busyDownloadingDocumentaryProofOverview = ref<boolean>(false);
const busyDownloadingDocumentaryProofZip      = ref<boolean>(false);
const messages                                = ref<string[]>([]);
const treekNormDialogVisible                  = ref(false);
const kpiGraphDialogVisible                   = ref(false);
const documentaryProofDialogVisible           = ref(false);
const documentaryProofAsZip                   = ref<boolean>(false);
const dbVersion                               = ref<string>('unknown');
const cacheStats                              = ref<any>(null);
const voortgang                               = ref<Voortgang | undefined>(undefined);

const blockFurtherActions = computed<boolean>(() =>
    busyDownloadingReportItemsExcel.value
    || busyDownloadingReportItemsCsv.value
    || busyDownloadingKpiMilestones.value
    || busyDownloadingTreekNormCsv.value
    || busyDownloadingDatabase.value
    || busyDownloadingDatabase_legacy.value
    || busyVerifyDatabase.value
    || busyDownloadingExprDump.value
    || busyErasingDatabase.value
    || busyUploadingDatabase.value
    || busyDownloadingBackup.value
    || busyDownloadingDocumentaryProofOverview.value
    || busyDownloadingDocumentaryProofZip.value,
);

onMounted(async () => {
    try {
        loading.value        = true;
        reportingState.value = await getStateForReporters();
        dbVersion.value      = await getDatabaseVersionFromDb();
        await refreshMessages();
        await refreshCacheStats();
    } catch (e) {
        console.error('huh', e);
    } finally {
        loading.value = false;
    }
});

function userInfo(msg: string) {
    ElMessage({
        message : msg,
        type    : 'success',
        duration: 15 * 1000,
    });
}

function userError(msg: string) {
    console.error(`${msg}`);
    ElMessage({
        message : msg,
        type    : 'error',
        duration: 15 * 1000,
    });
}

function heeftMessage(x: unknown): x is {message: string} {
    return typeof x === 'object' && x !== null && typeof (x as {message?: unknown}).message === 'string';
}

async function doDownloadReportingItemsExcel() {
    busyDownloadingReportItemsExcel.value = true;
    const response                        = await exportReportItemsForAdmin();
    if (response.ok) {
        await downloadFile(response, `${generateDateId()}-zzc-report.xlsx`);
    } else {
        const data = await new Response(response.body).json();
        ElMessage({
            message: `Failed: ${data.message}`,
            type   : 'error',
        });
    }
    busyDownloadingReportItemsExcel.value = false;
}

async function doDownloadReportingItemsCsv() {
    busyDownloadingReportItemsCsv.value = true;
    const response                      = await exportReportItemsForAdminCsv();
    if (response.ok) {
        await downloadFile(response, `${generateDateId()}-zzc-report.csv`);
    } else {
        const data = await new Response(response.body).json();
        ElMessage({
            message: `Failed: ${data.message}`,
            type   : 'error',
        });
    }
    busyDownloadingReportItemsCsv.value = false;
}

async function doDownloadKpiMilestones() {
    busyDownloadingKpiMilestones.value = true;
    const response                     = await exportKpiMilestonesForAdmin();
    if (response.ok) {
        await downloadFile(response, `${generateDateId()}-zzc-kpi-milestones.xlsx`);
    } else {
        const data = await new Response(response.body).json();
        ElMessage({
            message: `Failed: ${data.message}`,
            type   : 'error',
        });
    }
    busyDownloadingKpiMilestones.value = false;
}

async function startDownloadTreekNormComplianceCsv() {
    busyDownloadingTreekNormCsv.value = true;
    treekNormDialogVisible.value      = true;
}

async function continueDownloadTreekNormComplianceCsv(period?: CabPeriod) {
    treekNormDialogVisible.value = false;
    if (period) {
        const response = await tnComplianceCsvExport(period);
        if (!response.ok) {
            const data = await new Response(response.body).json();
            userError(`Failed: ${data.message}`);
        } else {
            await downloadFile(response, `${generateDateId()}-zzc-treekNormNaleving-${period}.csv`);
        }
    }
    busyDownloadingTreekNormCsv.value = false;
}

async function startDownloadDocumentaryProofOverview(asZip: boolean = false) {
    documentaryProofAsZip.value         = asZip;
    documentaryProofDialogVisible.value = true;
}

async function continueDownloadDocumentaryProofOverview(period?: CabPeriod) {
    documentaryProofDialogVisible.value = false;
    if (period) {
        const asZip: boolean     = documentaryProofAsZip.value;
        const busy: Ref<boolean> = asZip ? busyDownloadingDocumentaryProofZip : busyDownloadingDocumentaryProofOverview;
        busy.value               = true;
        try {
            const response: Response = asZip ? await exportDocumentaryProofOverviewZip(period) : await exportDocumentaryProofOverview(period);
            if (!response.ok) {
                const data = await new Response(response.body).json();
                userError(`Failed: ${data.message}`);
            } else {
                await downloadFile(response, `${generateDateId()}-zzc-documentaryProofOverview-${period}.${asZip ? 'zip' : 'pdf'}`);
            }
        } catch (e) {
            userError(`Failed: ${e}`);
        } finally {
            busy.value = false;
        }
    }
}

function generateListOfPastPeriods(n: number, unit: PeriodEnum = PeriodEnum.Q): CabPeriod[] {
    const periods: CabPeriod[] = [];
    // Add 6 months to the current date
    const now                  = new Date();
    now.setMonth(now.getMonth() + CabPeriod.monthsPer(unit)); //include current period
    let currentPeriod = CabPeriod.of(now, unit);
    for (let i = 0; i < n; i++) {
        currentPeriod = currentPeriod.previousPeriod();
        periods.push(currentPeriod);
    }
    return periods;
}

async function onVerifyDatabase() {
    busyVerifyDatabase.value = true;
    const response           = await verifyDatabase();
    userInfo(`Database completely synced (${response})`);
    await refreshMessages();
    busyVerifyDatabase.value = false;
}

async function onExpressionDump() {
    busyDownloadingExprDump.value = true;
    const response                = await expressionDump();
    await downloadFile(response, `${generateDateId()}-zzc-expression-dump.json`);
    busyDownloadingExprDump.value = false;
}

async function onEraseData() {
    busyErasingDatabase.value = true;
    try {
        // 'OK' when the erasing admin kept his session, otherwise 'OK, maar u bent uitgelogd...'
        const res: unknown = await devEraseData();
        if (typeof res === 'string' && res.startsWith('OK')) {
            userInfo(`Database completely erased (${res})`);
        } else {
            userError(`Database erase failed: ${res}`);
        }
        await refreshMessages();
    } catch (e) {
        userError(`Database erase failed: ${e}`);
    } finally {
        busyErasingDatabase.value = false;
    }
}

async function onUploadDb() {
    busyUploadingDatabase.value = true;
    const input                 = document.createElement('input');
    input.type                  = 'file';
    input.style.display         = 'none';
    input.addEventListener('change', async (event: Event) => {
        const target = event.target as HTMLInputElement;
        const file   = target.files?.[0] ?? null;
        if (file) {
            const formData = new FormData();
            formData.append('file', file);
            try {
                const antwoord: UploadAntwoord = await uploadMetVoortgang('/api/admin/restore', formData, (v: Voortgang) => voortgang.value = v);
                const melding: string           = heeftMessage(antwoord.body) ? antwoord.body.message : `status ${antwoord.status}`;
                if (antwoord.status === 200) {
                    userInfo(`Database completely replaced and restored (${melding})`);
                } else {
                    userError(`Database restored failed: (${melding})`);
                }
                await refreshMessages();
            } catch (e) {
                userError(`Database restored failed: (${e})`);
            } finally {
                voortgang.value             = undefined;
                busyUploadingDatabase.value = false;
            }
        }
    });
    document.body.appendChild(input);
    input.click();
}

async function onDownloadBackup() {
    const tag = await askForTag('Download Backup');
    if (tag) {
        busyDownloadingBackup.value = true;
        const response              = await backup();
        await downloadFile(response, `${generateDateId()}-zzc-backup-${tag}.zip`);
        busyDownloadingBackup.value = false;
    }
}

async function refreshMessages() {
    const m: Record<string, string[]> = await getMessages();
    messages.value                    = Object.entries(m)
        .filter(([_key, lines]) => lines.length > 0)
        .flatMap(([key, lines]) => [`===== ${key} =====`, ... lines]);
}

async function onClearMessages() {
    await clearMessages();
    await refreshMessages();
}

async function refreshCacheStats() {
    try {
        cacheStats.value = await getCacheStats();
    } catch (e) {
        console.error('Failed to load cache stats:', e);
        userError('Failed to load cache stats');
    }
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

type ReportingOption = {
    label: string;
    value: AvailabilityState;
};
const reportingOptions: Array<ReportingOption> = [
    {
        label: 'Gesloten',
        value: AvailabilityState.closed,
    },
    {
        label: 'Alleen Lezen',
        value: AvailabilityState.read,
    },
    {
        label: 'Rapporteren',
        value: AvailabilityState.write,
    },
];

watch(reportingState, (_is, was) => {
    if (was !== undefined) {
        setAvailabilityForReporters(reportingState.value);
    }
});
</script>

<template>
    <div class="flex justify-start items-start mt-10 gap-8">
        <!-- System Statistics Column (with padding) -->
        <div class="flex flex-col p-6">
            <!-- Cache Stats Section -->
            <div class="max-w-md w-64">
                <h3 class="text-lg font-semibold mb-3 text-center">System Statistics</h3>
                <div class="grid grid-cols-1 gap-4 text-sm">
                    <div class="bg-orange-50 p-3 rounded">
                        <h4 class="font-semibold text-orange-800 mb-2">Memory</h4>
                        <div class="space-y-1">
                            <div class="flex justify-between">
                                Used Heap
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatMemSizeAligned(10, cacheStats.usedHeap) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                            <div class="flex justify-between">
                                Max Heap
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatMemSizeAligned(10, cacheStats.maxHeap) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                            <div class="flex justify-between">
                                Used Memory
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatMemSizeAligned(10, cacheStats.usedMemory) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                            <div class="flex justify-between">
                                Max Memory
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatMemSizeAligned(10, cacheStats.maxMemory) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="bg-blue-50 p-3 rounded">
                        <h4 class="font-semibold text-blue-800 mb-2">Neo4j Cache</h4>
                        <div class="space-y-1">
                            <div class="flex justify-between">
                                Total Requests
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatIntegerAligned(10, cacheStats.totalRequests) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                            <div class="flex justify-between">
                                Hits
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatIntegerAligned(10, cacheStats.hits) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                            <div class="flex justify-between">
                                Misses
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatIntegerAligned(10, cacheStats.misses) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                            <div class="flex justify-between">
                                Hit Rate
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatPercentageAligned(10, cacheStats.hitRate) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                            <div class="flex justify-between">
                                Current Size
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatIntegerAligned(10, cacheStats.currentSize) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                            <div class="flex justify-between">
                                Memory Used
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatMemSizeAligned(10, cacheStats.memoryUsed) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                            <div class="flex justify-between">
                                Additions
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatIntegerAligned(10, cacheStats.additions) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                            <div class="flex justify-between">
                                Updates
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatIntegerAligned(10, cacheStats.updates) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                            <div class="flex justify-between">
                                Removals
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatIntegerAligned(10, cacheStats.removals) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                            <div class="flex justify-between">
                                Clears
                                <span class="font-mono pl-2">
                                    <template v-if="cacheStats">
                                        {{ formatIntegerAligned(10, cacheStats.clears) }}
                                    </template>
                                    <template v-else>...</template>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="mt-3 text-center">
                    <el-button
                        type="info"
                        size="small"
                        @click="refreshCacheStats"
                    >
                        Refresh
                    </el-button>
                </div>
            </div>
        </div>

        <!-- Main Admin Controls Column (centered in remaining space) -->
        <div class="flex flex-col space-y-4 flex-1 justify-center items-center">
            <div class="flex flex-col space-y-4 justify-center items-center p-5 border-transparent border-2">
                <div>Rapportage Status:</div>
                <el-segmented
                    v-model="reportingState"
                    :options="reportingOptions"
                    :disabled="loading"
                >
                    <template #default="{ item }: { item: any }">
                        <div class="flex flex-row items-center gap-2 p-2">
                            <LockedIcon v-if="item.value===AvailabilityState.closed"/>
                            <ReadOnlyIcon v-if="item.value===AvailabilityState.read"/>
                            <UnlockedIcon v-if="item.value===AvailabilityState.write"/>
                            <div>{{ item.label }}</div>
                        </div>
                    </template>
                </el-segmented>

                <div class="w-full">
                    <el-divider class="border-black my-2"/>
                </div>

                <div class="flex gap-3 items-center w-96">
                    <el-button type="primary" size="large"
                               @click="doDownloadReportingItemsExcel"
                               :disabled="blockFurtherActions"
                               :loading="busyDownloadingReportItemsExcel"
                               class="flex-1"
                    >
                        Download Report (Excel)
                    </el-button>
                    <el-button type="primary" size="large"
                               @click="doDownloadReportingItemsCsv"
                               :disabled="blockFurtherActions"
                               :loading="busyDownloadingReportItemsCsv"
                               class="w-20 flex-shrink-0"
                    >
                        CSV
                    </el-button>
                </div>

                <el-button type="primary" size="large"
                           @click="doDownloadKpiMilestones"
                           :disabled="blockFurtherActions"
                           :loading="busyDownloadingKpiMilestones"
                           class="w-96">
                    Download KPI's & Mijlpalen (Excel)
                </el-button>

                <el-button type="primary" size="large"
                           @click="startDownloadTreekNormComplianceCsv"
                           :disabled="blockFurtherActions"
                           :loading="busyDownloadingTreekNormCsv"
                           class="w-96">
                    Download Treeknorm Naleving (CSV)
                </el-button>

                <el-button type="primary" size="large"
                           @click="startDownloadDocumentaryProofOverview()"
                           :disabled="blockFurtherActions"
                           :loading="busyDownloadingDocumentaryProofOverview"
                           class="w-96">
                    Download Documentary Proof Overview (PDF)
                </el-button>

                <el-button type="primary" size="large"
                           @click="startDownloadDocumentaryProofOverview(true)"
                           :disabled="blockFurtherActions"
                           :loading="busyDownloadingDocumentaryProofZip"
                           class="w-96">
                    Download Documentary Proof Overview (ZIP)
                </el-button>

                <el-button type="primary" size="large"
                           @click="onExpressionDump()"
                           :disabled="blockFurtherActions"
                           :loading="busyDownloadingExprDump"
                           class="w-96"
                >
                    Download Expressions (JSON)
                </el-button>

                <el-button type="primary" size="large"
                           @click="kpiGraphDialogVisible = true"
                           :disabled="blockFurtherActions"
                           class="w-96"
                           title="Toon de afhankelijkheden tussen KPI-expressies als grafiek"
                >
                    KPI Afhankelijkheden
                </el-button>

                <div class="w-full">
                    <el-divider class="border-black my-2"/>
                </div>

                <p>Current DB version: {{ dbVersion }}</p>

                <el-button type="primary" size="large"
                           @click="onVerifyDatabase"
                           :disabled="blockFurtherActions"
                           :loading="busyVerifyDatabase"
                           class="w-96"
                           title="Verify the database (can take a while)"
                >
                    Verify
                </el-button>

                <el-button type="primary" size="large"
                           @click="onDownloadBackup"
                           :disabled="blockFurtherActions"
                           :loading="busyDownloadingBackup"
                           class="w-96"
                           title="Download a zipfile with a backup of the database"
                >
                    Backup (ZIP)
                </el-button>

            </div>

            <div class="relative flex flex-col space-y-4 justify-center items-center p-5 border-orange-500 border-2">
                <el-popconfirm
                    title="Alle info in de huidige database gaat verloren. Weet je het zeker?"
                    width="450px"
                    @confirm="onEraseData"
                    confirm-button-text="JA"
                    cancel-button-text="Stop"
                    confirm-button-type="danger"
                    :hide-icon="true"
                >
                    <template #reference>
                        <el-button type="warning" size="large"
                                   :disabled="blockFurtherActions"
                                   :loading="busyErasingDatabase"
                                   class="w-96"
                                   title="Clear the database completely"
                        >
                            Erase All Data
                        </el-button>
                    </template>
                </el-popconfirm>

                <el-popconfirm
                    title="Alle info in de huidige database gaat verloren. Weet je het zeker?"
                    width="450px"
                    @confirm="onUploadDb"
                    confirm-button-text="JA"
                    cancel-button-text="Stop"
                    confirm-button-type="danger"
                    :hide-icon="true"
                >
                    <template #reference>
                        <el-button type="warning" size="large"
                                   :disabled="blockFurtherActions"
                                   :loading="busyUploadingDatabase"
                                   class="w-96"
                                   title="Clear the database and overwrite it with a backup in the zip you will upload"
                        >
                            Restore (ZIP)
                        </el-button>
                    </template>
                </el-popconfirm>

                <VoortgangView :voortgang="voortgang"/>

                <span
                    class="absolute top-[-30px] left-[170px] bg-orange-500 text-white text-sm px-4 py-0.5 rounded cursor-default">
                    Danger Zone
                </span>
            </div>
        </div>
    </div>

    <KpiGraphDialog v-model="kpiGraphDialogVisible"/>

    <el-dialog title="Van welk kwartaal wil je de wachttijden?"
               v-model="treekNormDialogVisible"
               width="30%"
               center
               @close="continueDownloadTreekNormComplianceCsv(undefined)"
    >
        <div class="flex flex-col items-center">
            <el-button
                type="primary"
                class="my-2"
                v-for="item in generateListOfPastPeriods(8, PeriodEnum.Q)"
                :key="item.uuid"
                @click="continueDownloadTreekNormComplianceCsv(item)">
                {{ item.toString() }}
            </el-button>
        </div>
    </el-dialog>

    <el-dialog title="Van welke reporting moment wil je het overzicht?"
               v-model="documentaryProofDialogVisible"
               width="30%"
               center
               @close="continueDownloadDocumentaryProofOverview(undefined)"
    >
        <div class="flex flex-col items-center">
            <el-button
                type="primary"
                class="my-2"
                v-for="item in generateListOfPastPeriods(4, PeriodEnum.H)"
                :key="item.uuid"
                @click="continueDownloadDocumentaryProofOverview(item)">
                {{ item.toString() }}
            </el-button>
        </div>
    </el-dialog>

    <div class="flex flex-col justify-center items-center mt-6 px-4" v-if="1<messages.length">
        <div
            class="w-full h-80 overflow-auto border-2 my-4 mx-10 p-4 rounded bg-black border-white font-mono text-xs whitespace-nowrap text-green-400">
            <p class="p-1"
               v-for="(l, i) in messages"
               :key="i"
            >
                {{ l }}
            </p>
        </div>
        <el-button type="primary" size="large"
                   @click="onClearMessages();"
                   class="w-44 mr-3"
        >
            Clear Messages
        </el-button>

    </div>
</template>
<style scoped>
/*noinspection CssUnusedSymbol*/
.flex.flex-col .el-button + .el-button {
    margin-left: 0 !important;
}
</style>
